import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, AddCircleOutline } from '@mui/icons-material';
import api from '../../../services/api';
import styles from '../../../assets/styles/LayoutShared.module.css';

// ─── Tipos ────────────────────────────────────────────────────────────────────

interface Area {
  uid_area: string;
  nome: string;
  tipo: string;
  area_hectares: string | null;
  auto_cadastrado: boolean;
  observacao?: string;
}

interface Veiculo {
  uid_veiculo: string;
  modelo: string;
  tipo: string | null;
  marca: string | null;
  ano?: string | null;
  placa?: string | null;
  observacao?: string | null;
}

interface Cultura {
  uid_cultura: string;
  nome: string;
  descricao: string | null;
}

const AREA_TIPOS = [
  'talhao','estufa','canteiro','pomar','viveiro','horta','armazem',
  'prateleira','armario','pasto','piquete','curral','galinheiro',
  'chiqueiro','tanque','acude','galpao','area',
];

// ─── Hook mobile ──────────────────────────────────────────────────────────────

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

// ─── Estilos compartilhados ───────────────────────────────────────────────────

const fieldStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 8,
  border: '1px solid #ddd', outline: 'none', fontSize: '0.9rem',
  boxSizing: 'border-box',
};

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', fontSize: '0.78rem', color: '#888', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>
    {children}
  </label>
);

// ─── ModalWrapper — adapta entre centered (desktop) e bottom sheet (mobile) ───

const ModalWrapper: React.FC<{ children: React.ReactNode; onClose: () => void; isMobile: boolean }> = ({ children, onClose, isMobile }) => (
  <div
    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', zIndex: 9999 }}
    onClick={onClose}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: '#fff',
        borderRadius: isMobile ? '20px 20px 0 0' : 16,
        padding: isMobile ? '24px 20px 36px' : '28px 32px',
        boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
        maxWidth: isMobile ? undefined : 500,
        width: isMobile ? '100%' : '90%',
        position: 'relative',
        maxHeight: isMobile ? '90vh' : undefined,
        overflowY: isMobile ? 'auto' : undefined,
      }}
    >
      {isMobile && (
        <div style={{ width: 40, height: 4, borderRadius: 2, background: '#e0e0e0', margin: '0 auto 20px' }} />
      )}
      {!isMobile && (
        <button
          onClick={onClose}
          style={{ position: 'absolute', top: 12, right: 14, background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: '#bbb', lineHeight: 1, padding: '4px 6px', borderRadius: 6 }}
          onMouseEnter={e => (e.currentTarget.style.color = '#555')}
          onMouseLeave={e => (e.currentTarget.style.color = '#bbb')}
        >✕</button>
      )}
      {children}
    </div>
  </div>
);

const ModalActions: React.FC<{ onClose: () => void; onSave: () => void; saving: boolean }> = ({ onClose, onSave, saving }) => (
  <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
    <button onClick={onClose} disabled={saving} style={{ flex: 1, padding: '11px', borderRadius: 8, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontWeight: 600, color: '#555' }}>
      Cancelar
    </button>
    <button onClick={onSave} disabled={saving} style={{ flex: 1, padding: '11px', borderRadius: 8, border: 'none', background: saving ? '#aaa' : '#00b37e', color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 700 }}>
      {saving ? 'Salvando...' : 'Salvar'}
    </button>
  </div>
);

// ─── Modal Área ───────────────────────────────────────────────────────────────

interface ModalAreaProps { area: Area; onClose: () => void; onSave: (updated: Area) => void; isNew?: boolean; isMobile: boolean; }

const ModalEditArea: React.FC<ModalAreaProps> = ({ area, onClose, onSave, isNew, isMobile }) => {
  const [form, setForm] = useState({ ...area });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.nome?.trim()) { setError('O nome da área é obrigatório.'); return; }
    setSaving(true); setError('');
    try {
      const uidFazenda = localStorage.getItem('uid_unidade_producao') || '';
      const payload = { nome: form.nome, tipo: form.tipo || 'area', area_hectares: form.area_hectares ? parseFloat(String(form.area_hectares)) : null, observacao: form.observacao || null };
      if (isNew) {
        const res = await api.post('/agro/areas', { ...payload, uid_unidade_producao: uidFazenda });
        onSave(res.data.data);
      } else {
        await api.patch(`/agro/areas/${area.uid_area}`, payload);
        onSave(form);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao salvar. Tente novamente.');
    } finally { setSaving(false); }
  };

  return (
    <ModalWrapper onClose={onClose} isMobile={isMobile}>
      <h3 style={{ margin: '0 0 20px', color: '#333', fontSize: isMobile ? '1rem' : '1.1rem' }}>{isNew ? '➕ Nova Área' : '✏️ Editar Área'}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
        <div style={{ gridColumn: isMobile ? undefined : 'span 2' }}>
          <FieldLabel>Nome</FieldLabel>
          <input style={fieldStyle} value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} />
        </div>
        <div>
          <FieldLabel>Tipo</FieldLabel>
          <select style={fieldStyle} value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
            {AREA_TIPOS.map(t => <option key={t} value={t} style={{ textTransform: 'capitalize' }}>{t}</option>)}
          </select>
        </div>
        <div>
          <FieldLabel>Hectares</FieldLabel>
          <input style={fieldStyle} type="number" step="0.01" value={form.area_hectares || ''} onChange={e => setForm(f => ({ ...f, area_hectares: e.target.value }))} placeholder="Ex: 12.5" />
        </div>
        <div style={{ gridColumn: isMobile ? undefined : 'span 2' }}>
          <FieldLabel>Observação</FieldLabel>
          <textarea style={{ ...fieldStyle, resize: 'vertical' }} rows={2} value={form.observacao || ''} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} />
        </div>
      </div>
      {error && <p style={{ color: '#e74c3c', fontSize: '0.85rem', margin: '8px 0 0' }}>{error}</p>}
      <ModalActions onClose={onClose} onSave={handleSave} saving={saving} />
    </ModalWrapper>
  );
};

// ─── Modal Veículo ────────────────────────────────────────────────────────────

interface ModalVeiculoProps { veiculo: Veiculo; onClose: () => void; onSave: (updated: Veiculo) => void; isNew?: boolean; isMobile: boolean; }

const ModalEditVeiculo: React.FC<ModalVeiculoProps> = ({ veiculo, onClose, onSave, isNew, isMobile }) => {
  const [form, setForm] = useState({ ...veiculo });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    if (!form.modelo?.trim()) { setError('O modelo do veículo é obrigatório.'); return; }
    setSaving(true); setError('');
    try {
      const uidFazenda = localStorage.getItem('uid_unidade_producao') || '';
      const payload = { modelo: form.modelo, tipo: form.tipo || null, marca: form.marca || null, ano: form.ano ? parseInt(String(form.ano)) : null, placa: form.placa || null, observacao: form.observacao || null };
      if (isNew) {
        const res = await api.post('/agro/veiculos', { ...payload, uid_unidade_producao: uidFazenda });
        onSave(res.data.data);
      } else {
        await api.patch(`/agro/veiculos/${veiculo.uid_veiculo}`, payload);
        onSave(form);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao salvar. Tente novamente.');
    } finally { setSaving(false); }
  };

  return (
    <ModalWrapper onClose={onClose} isMobile={isMobile}>
      <h3 style={{ margin: '0 0 20px', color: '#333', fontSize: isMobile ? '1rem' : '1.1rem' }}>{isNew ? '➕ Novo Veículo' : '✏️ Editar Veículo'}</h3>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>
        <div style={{ gridColumn: isMobile ? undefined : 'span 2' }}>
          <FieldLabel>Modelo</FieldLabel>
          <input style={fieldStyle} value={form.modelo || ''} onChange={e => setForm(f => ({ ...f, modelo: e.target.value }))} />
        </div>
        <div>
          <FieldLabel>Marca</FieldLabel>
          <input style={fieldStyle} value={form.marca || ''} onChange={e => setForm(f => ({ ...f, marca: e.target.value }))} />
        </div>
        <div>
          <FieldLabel>Tipo</FieldLabel>
          <input style={fieldStyle} value={form.tipo || ''} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))} placeholder="Ex: trator, caminhão..." />
        </div>
        <div>
          <FieldLabel>Ano</FieldLabel>
          <input style={fieldStyle} value={form.ano || ''} onChange={e => setForm(f => ({ ...f, ano: e.target.value }))} type="number" placeholder="Ex: 2020" />
        </div>
        <div>
          <FieldLabel>Placa</FieldLabel>
          <input style={fieldStyle} value={form.placa || ''} onChange={e => setForm(f => ({ ...f, placa: e.target.value }))} placeholder="ABC-1234" />
        </div>
        <div style={{ gridColumn: isMobile ? undefined : 'span 2' }}>
          <FieldLabel>Observação</FieldLabel>
          <textarea style={{ ...fieldStyle, resize: 'vertical' }} rows={2} value={form.observacao || ''} onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} />
        </div>
      </div>
      {error && <p style={{ color: '#e74c3c', fontSize: '0.85rem', margin: '8px 0 0' }}>{error}</p>}
      <ModalActions onClose={onClose} onSave={handleSave} saving={saving} />
    </ModalWrapper>
  );
};

// ─── Cards Mobile ─────────────────────────────────────────────────────────────

const AreaCard: React.FC<{ area: Area; onEdit: () => void }> = ({ area, onEdit }) => (
  <div style={{
    background: '#fff', borderRadius: 14, padding: '14px 16px', marginBottom: 10,
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
  }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#222', marginBottom: 5 }}>
        📍 {area.nome}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {area.tipo && (
          <span style={{ background: '#f0f4ff', color: '#3a5bd9', padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600, textTransform: 'capitalize' }}>
            {area.tipo}
          </span>
        )}
        {area.area_hectares && (
          <span style={{ background: '#f0fff8', color: '#00b37e', padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600 }}>
            {parseFloat(area.area_hectares).toFixed(1)} ha
          </span>
        )}
        <span style={{ background: area.auto_cadastrado ? '#fff8e1' : '#f0fff8', color: area.auto_cadastrado ? '#f39c12' : '#00b37e', padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600 }}>
          {area.auto_cadastrado ? '📱 WhatsApp' : '✅ Manual'}
        </span>
      </div>
    </div>
    <button
      onClick={onEdit}
      style={{
        background: '#f0fff8', border: '1px solid #00b37e', cursor: 'pointer',
        borderRadius: 8, padding: '8px 10px', color: '#00b37e',
        display: 'flex', alignItems: 'center', flexShrink: 0,
      }}
    >
      <Edit fontSize="small" />
    </button>
  </div>
);

const VeiculoCard: React.FC<{ veiculo: Veiculo; onEdit: () => void }> = ({ veiculo, onEdit }) => (
  <div style={{
    background: '#fff', borderRadius: 14, padding: '14px 16px', marginBottom: 10,
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
  }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#222', marginBottom: 5 }}>
        🚜 {veiculo.modelo}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {veiculo.marca && (
          <span style={{ background: '#f5f5f5', color: '#555', padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600 }}>
            {veiculo.marca}
          </span>
        )}
        {veiculo.tipo && (
          <span style={{ background: '#fff8e1', color: '#f39c12', padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600 }}>
            {veiculo.tipo}
          </span>
        )}
        {veiculo.ano && (
          <span style={{ background: '#f5f5f5', color: '#666', padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600 }}>
            {veiculo.ano}
          </span>
        )}
        {veiculo.placa && (
          <span style={{ background: '#f0f4ff', color: '#3a5bd9', padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700, letterSpacing: 1 }}>
            {veiculo.placa}
          </span>
        )}
      </div>
    </div>
    <button
      onClick={onEdit}
      style={{
        background: '#fff8e1', border: '1px solid #f39c12', cursor: 'pointer',
        borderRadius: 8, padding: '8px 10px', color: '#f39c12',
        display: 'flex', alignItems: 'center', flexShrink: 0,
      }}
    >
      <Edit fontSize="small" />
    </button>
  </div>
);

const CulturaCard: React.FC<{ cultura: Cultura }> = ({ cultura }) => (
  <div style={{
    background: '#fff', borderRadius: 14, padding: '14px 16px', marginBottom: 10,
    boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
    display: 'flex', alignItems: 'center', gap: 12,
  }}>
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#222', marginBottom: cultura.descricao ? 4 : 0 }}>
        🌱 {cultura.nome}
      </div>
      {cultura.descricao && (
        <span style={{ background: '#f0fff4', color: '#27ae60', padding: '2px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600 }}>
          {cultura.descricao}
        </span>
      )}
    </div>
  </div>
);

// ─── Componente Principal ─────────────────────────────────────────────────────

const AgroFazenda = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();

  const [areas, setAreas] = useState<Area[]>([]);
  const [veiculos, setVeiculos] = useState<Veiculo[]>([]);
  const [culturas, setCulturas] = useState<Cultura[]>([]);
  const [aba, setAba] = useState<'areas' | 'veiculos' | 'culturas' | 'produtos'>('areas');

  const [editArea, setEditArea]       = useState<Area | null>(null);
  const [editVeiculo, setEditVeiculo] = useState<Veiculo | null>(null);
  const [novaArea, setNovaArea]       = useState(false);
  const [novoVeiculo, setNovoVeiculo] = useState(false);

  const AREA_VAZIA: Area = { uid_area: '', nome: '', tipo: 'talhao', area_hectares: null, auto_cadastrado: false, observacao: '' };
  const VEICULO_VAZIO: Veiculo = { uid_veiculo: '', modelo: '', tipo: '', marca: '', ano: '', placa: '', observacao: '' };

  useEffect(() => {
    const uidFazenda = localStorage.getItem('uid_unidade_producao') || '';
    const q = uidFazenda ? `?uid_unidade_producao=${uidFazenda}` : '';
    api.get(`/agro/areas${q}`).then(r => setAreas(r.data.data || [])).catch(() => {});
    api.get(`/agro/veiculos${q}`).then(r => setVeiculos(r.data.data || [])).catch(() => {});
    api.get(`/agro/culturas${q}`).then(r => setCulturas(r.data.data || [])).catch(() => {});
  }, []);

  const abas = [
    { key: 'areas',    label: isMobile ? `📍 Áreas` : `📍 Áreas (${areas.length})`,       count: areas.length },
    { key: 'veiculos', label: isMobile ? `🚜 Veículos` : `🚜 Veículos (${veiculos.length})`, count: veiculos.length },
    { key: 'culturas', label: isMobile ? `🌱 Culturas` : `🌱 Culturas (${culturas.length})`, count: culturas.length },
    { key: 'produtos', label: isMobile ? `📦 Produtos` : `📦 Produtos`, count: 0 },
  ] as const;

  const EmptyState = ({ msg }: { msg: string }) => (
    <div style={{ textAlign: 'center', padding: '48px 0', color: '#bbb' }}>
      <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>📭</div>
      <p style={{ margin: 0, fontSize: '0.95rem' }}>{msg}</p>
    </div>
  );

  // ─── Render Mobile ────────────────────────────────────────────────────────

  if (isMobile) {
    return (
      <div style={{ background: '#f4f6f8', minHeight: '100vh', paddingBottom: 80, width: '100%' }}>

        {/* Header sticky */}
        <div style={{
          background: '#fff', padding: '14px 16px 0',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          position: 'sticky', top: 0, zIndex: 100,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <h1 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#00b37e' }}>
              Minha Fazenda
            </h1>
            {aba === 'areas' && (
              <button onClick={() => setNovaArea(true)} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '7px 12px', borderRadius: 8, border: 'none',
                background: '#00b37e', color: '#fff', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.82rem',
              }}>
                <AddCircleOutline style={{ fontSize: '1rem' }} /> Nova Área
              </button>
            )}
            {aba === 'veiculos' && (
              <button onClick={() => setNovoVeiculo(true)} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '7px 12px', borderRadius: 8, border: 'none',
                background: '#f39c12', color: '#fff', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.82rem',
              }}>
                <AddCircleOutline style={{ fontSize: '1rem' }} /> Novo Veículo
              </button>
            )}
            {aba === 'produtos' && (
              <button onClick={() => navigate('/agro/productsHub/products/add')} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '7px 12px', borderRadius: 8, border: 'none',
                background: '#3498db', color: '#fff', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.82rem',
              }}>
                <AddCircleOutline style={{ fontSize: '1rem' }} /> Novo Produto
              </button>
            )}
          </div>

          {/* Abas mobile — pill tabs */}
          <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #f0f0f0' }}>
            {abas.map(a => (
              <button key={a.key} onClick={() => setAba(a.key)} style={{
                flex: 1, padding: '10px 4px', border: 'none', cursor: 'pointer',
                fontWeight: 600, fontSize: '0.8rem', background: 'transparent',
                color: aba === a.key ? '#00b37e' : '#999',
                borderBottom: aba === a.key ? '2px solid #00b37e' : '2px solid transparent',
                marginBottom: -2, transition: 'color 0.15s',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
              }}>
                {a.label}
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: aba === a.key ? '#00b37e' : '#bbb' }}>
                  {a.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Conteúdo */}
        <div style={{ padding: '12px 12px 0' }}>

          {aba === 'areas' && (
            <>
              {areas.length === 0
                ? <EmptyState msg="Nenhuma área cadastrada." />
                : areas.map(a => (
                    <AreaCard key={a.uid_area} area={a} onEdit={() => setEditArea(a)} />
                  ))
              }
            </>
          )}

          {aba === 'veiculos' && (
            <>
              {veiculos.length === 0
                ? <EmptyState msg="Nenhum veículo cadastrado." />
                : veiculos.map(v => (
                    <VeiculoCard key={v.uid_veiculo} veiculo={v} onEdit={() => setEditVeiculo(v)} />
                  ))
              }
            </>
          )}

          {aba === 'culturas' && (
            <>
              {culturas.length === 0
                ? <EmptyState msg="Nenhuma cultura registrada em eventos." />
                : culturas.map(c => (
                    <CulturaCard key={c.uid_cultura} cultura={c} />
                  ))
              }
            </>
          )}

          {aba === 'produtos' && (
            <div style={{ paddingTop: 8 }}>
              <button
                onClick={() => navigate('/agro/productsHub/products')}
                style={{
                  width: '100%', padding: '16px', borderRadius: 12,
                  border: '2px dashed #3498db', background: '#f0f8ff',
                  cursor: 'pointer', color: '#3498db', fontWeight: 700,
                  fontSize: '0.95rem', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', gap: 8,
                }}>
                📦 Ver todos os Produtos
              </button>
              <p style={{ textAlign: 'center', color: '#aaa', fontSize: '0.82rem', marginTop: 12 }}>
                Gerencie seu catálogo de produtos
              </p>
            </div>
          )}
        </div>

        {/* Modais (bottom sheet no mobile) */}
        {editArea && (
          <ModalEditArea isMobile area={editArea} onClose={() => setEditArea(null)}
            onSave={(updated) => { setAreas(prev => prev.map(a => a.uid_area === updated.uid_area ? updated : a)); setEditArea(null); }} />
        )}
        {editVeiculo && (
          <ModalEditVeiculo isMobile veiculo={editVeiculo} onClose={() => setEditVeiculo(null)}
            onSave={(updated) => { setVeiculos(prev => prev.map(v => v.uid_veiculo === updated.uid_veiculo ? updated : v)); setEditVeiculo(null); }} />
        )}
        {novaArea && (
          <ModalEditArea isMobile isNew area={AREA_VAZIA} onClose={() => setNovaArea(false)}
            onSave={(created) => { setAreas(prev => [...prev, created]); setNovaArea(false); }} />
        )}
        {novoVeiculo && (
          <ModalEditVeiculo isMobile isNew veiculo={VEICULO_VAZIO} onClose={() => setNovoVeiculo(false)}
            onSave={(created) => { setVeiculos(prev => [...prev, created]); setNovoVeiculo(false); }} />
        )}
      </div>
    );
  }

  // ─── Render Desktop (original) ────────────────────────────────────────────

  return (
    <div className={styles.container}>
      <h1 className={styles.greenTitle}>Minha Fazenda</h1>

      {/* Abas */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '2px solid #f0f0f0', paddingBottom: 12, alignItems: 'center' }}>
        {abas.map(a => (
          <button key={a.key} onClick={() => setAba(a.key)} style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontWeight: 600, fontSize: '0.9rem',
            background: aba === a.key ? '#00b37e' : '#f5f5f5',
            color: aba === a.key ? '#fff' : '#555',
          }}>{a.label}</button>
        ))}
        <div style={{ marginLeft: 'auto' }}>
          {aba === 'areas' && (
            <button onClick={() => setNovaArea(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, border: 'none', background: '#00b37e', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
              <AddCircleOutline fontSize="small" /> Nova Área
            </button>
          )}
          {aba === 'veiculos' && (
            <button onClick={() => setNovoVeiculo(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, border: 'none', background: '#00b37e', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
              <AddCircleOutline fontSize="small" /> Novo Veículo
            </button>
          )}
          {aba === 'produtos' && (
            <button onClick={() => navigate('/agro/productsHub/products/add')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 16px', borderRadius: 8, border: 'none', background: '#3498db', color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
              <AddCircleOutline fontSize="small" /> Novo Produto
            </button>
          )}
        </div>
      </div>

      {/* Áreas */}
      {aba === 'areas' && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead><tr>
              <th>Nome</th><th>Tipo</th><th style={{ textAlign: 'right' }}>Hectares</th>
              <th style={{ textAlign: 'center' }}>Origem</th>
              <th style={{ textAlign: 'center', width: 60 }}>Ações</th>
            </tr></thead>
            <tbody>
              {areas.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 30, color: '#999' }}>Nenhuma área cadastrada.</td></tr>}
              {areas.map((a) => (
                <tr key={a.uid_area}>
                  <td style={{ fontWeight: 600 }}>{a.nome}</td>
                  <td style={{ textTransform: 'capitalize', color: '#666' }}>{a.tipo || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{a.area_hectares ? `${parseFloat(a.area_hectares).toFixed(1)} ha` : '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    {a.auto_cadastrado
                      ? <span style={{ color: '#f39c12', fontSize: '0.8rem', fontWeight: 600 }}>📱 WhatsApp</span>
                      : <span style={{ color: '#00b37e', fontSize: '0.8rem', fontWeight: 600 }}>✅ Manual</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={() => setEditArea(a)} title="Editar área"
                      style={{ background: 'none', border: '1px solid #e0e0e0', cursor: 'pointer', borderRadius: 6, padding: '4px 8px', color: '#00b37e', display: 'inline-flex', alignItems: 'center', transition: 'all 0.15s' }}
                      onMouseEnter={e => { (e.currentTarget.style.background = '#f0fff8'); }}
                      onMouseLeave={e => { (e.currentTarget.style.background = 'none'); }}>
                      <Edit fontSize="small" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Veículos */}
      {aba === 'veiculos' && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead><tr>
              <th>Modelo</th><th>Tipo</th><th>Marca</th><th>Ano</th><th>Placa</th>
              <th style={{ textAlign: 'center', width: 60 }}>Ações</th>
            </tr></thead>
            <tbody>
              {veiculos.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: '#999' }}>Nenhum veículo cadastrado.</td></tr>}
              {veiculos.map((v) => (
                <tr key={v.uid_veiculo}>
                  <td style={{ fontWeight: 600 }}>🚜 {v.modelo}</td>
                  <td style={{ color: '#666' }}>{v.tipo || '—'}</td>
                  <td style={{ color: '#666' }}>{v.marca || '—'}</td>
                  <td style={{ color: '#666' }}>{v.ano || '—'}</td>
                  <td style={{ color: '#666' }}>{v.placa || '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <button onClick={() => setEditVeiculo(v)} title="Editar veículo"
                      style={{ background: 'none', border: '1px solid #e0e0e0', cursor: 'pointer', borderRadius: 6, padding: '4px 8px', color: '#00b37e', display: 'inline-flex', alignItems: 'center', transition: 'all 0.15s' }}
                      onMouseEnter={e => { (e.currentTarget.style.background = '#f0fff8'); }}
                      onMouseLeave={e => { (e.currentTarget.style.background = 'none'); }}>
                      <Edit fontSize="small" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Culturas */}
      {aba === 'culturas' && (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead><tr><th>Nome</th><th>Categoria</th></tr></thead>
            <tbody>
              {culturas.length === 0 && <tr><td colSpan={2} style={{ textAlign: 'center', padding: 30, color: '#999' }}>Nenhuma cultura registrada em eventos.</td></tr>}
              {culturas.map((c) => (
                <tr key={c.uid_cultura}>
                  <td style={{ fontWeight: 600 }}>🌱 {c.nome}</td>
                  <td style={{ color: '#666' }}>{c.descricao || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Produtos */}
      {aba === 'produtos' && (
        <div style={{ padding: '24px 0' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <button
              onClick={() => navigate('/agro/productsHub/products')}
              style={{
                flex: 1, minWidth: 200, padding: '24px', borderRadius: 14,
                border: '2px solid #3498db', background: '#f0f8ff',
                cursor: 'pointer', display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 10, transition: 'all 0.15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#dbeeff'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#f0f8ff'; }}>
              <span style={{ fontSize: '2rem' }}>📦</span>
              <span style={{ fontWeight: 700, color: '#3498db', fontSize: '1rem' }}>Cadastro de Produtos</span>
              <span style={{ fontSize: '0.82rem', color: '#888' }}>Gerenciar catálogo completo</span>
            </button>
            <button
              onClick={() => navigate('/agro/productsHub/category')}
              style={{
                flex: 1, minWidth: 200, padding: '24px', borderRadius: 14,
                border: '2px solid #9b59b6', background: '#f9f0ff',
                cursor: 'pointer', display: 'flex', flexDirection: 'column',
                alignItems: 'center', gap: 10, transition: 'all 0.15s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#eedeff'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#f9f0ff'; }}>
              <span style={{ fontSize: '2rem' }}>🏷️</span>
              <span style={{ fontWeight: 700, color: '#9b59b6', fontSize: '1rem' }}>Categorias</span>
              <span style={{ fontSize: '0.82rem', color: '#888' }}>Organizar por categoria</span>
            </button>
          </div>
        </div>
      )}

      {/* Modais desktop */}
      {editArea && (
        <ModalEditArea isMobile={false} area={editArea} onClose={() => setEditArea(null)}
          onSave={(updated) => { setAreas(prev => prev.map(a => a.uid_area === updated.uid_area ? updated : a)); setEditArea(null); }} />
      )}
      {editVeiculo && (
        <ModalEditVeiculo isMobile={false} veiculo={editVeiculo} onClose={() => setEditVeiculo(null)}
          onSave={(updated) => { setVeiculos(prev => prev.map(v => v.uid_veiculo === updated.uid_veiculo ? updated : v)); setEditVeiculo(null); }} />
      )}
      {novaArea && (
        <ModalEditArea isMobile={false} isNew area={AREA_VAZIA} onClose={() => setNovaArea(false)}
          onSave={(created) => { setAreas(prev => [...prev, created]); setNovaArea(false); }} />
      )}
      {novoVeiculo && (
        <ModalEditVeiculo isMobile={false} isNew veiculo={VEICULO_VAZIO} onClose={() => setNovoVeiculo(false)}
          onSave={(created) => { setVeiculos(prev => [...prev, created]); setNovoVeiculo(false); }} />
      )}
    </div>
  );
};

export default AgroFazenda;